/**
 * React Query hooks for SMS messages API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '@/lib/firebase';
import type { SmsMessagesResponse } from '@/types/sms';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function fetchMessages(prospectId: string): Promise<SmsMessagesResponse> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/prospects/${prospectId}/messages`, { headers });
  if (!res.ok) throw new Error('Failed to fetch messages');
  return res.json();
}

async function sendMessage(prospectId: string, body: string): Promise<{ status: string; message_id: string }> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/prospects/${prospectId}/messages`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Failed to send message');
  }
  return res.json();
}

export function useMessages(prospectId: string | null) {
  return useQuery({
    queryKey: ['messages', prospectId],
    queryFn: () => fetchMessages(prospectId!),
    enabled: !!prospectId,
    refetchInterval: 30000, // Auto-refetch every 30s
  });
}

export function useSendMessage(prospectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: string) => sendMessage(prospectId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', prospectId] });
    },
  });
}
